// Decryption utilities compatible with Python's Fernet encryption

let SECRET_KEY;
try {
  SECRET_KEY = typeof process !== 'undefined' && process.env 
    ? process.env.GROWTHBOOK_API_RESPONSE_DECRYPTION_KEY 
    : undefined;
} catch (e) {
  SECRET_KEY = undefined;
}

if (!SECRET_KEY && typeof window !== 'undefined') {
  console.error('[Decryption] GROWTHBOOK_API_RESPONSE_DECRYPTION_KEY not found in environment!');
}

// Base64 decode using browser's atob or Node.js Buffer
function base64ToBytes(base64String) {
  if (typeof atob !== 'undefined') {
    const binaryString = atob(base64String);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  } 
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(base64String, 'base64');
  } 
  throw new Error('No base64 decoder available');
}

// Decrypt Fernet-encrypted response from backend
async function decryptAccountInfo(encryptedResponse, secretKey) {
  try {
    // Hash the secret key
    const encoder = new TextEncoder();
    const secretBytes = encoder.encode(secretKey);
    
    const hashBuffer = await crypto.subtle.digest('SHA-256', secretBytes);
    const hashBytes = new Uint8Array(hashBuffer);

    // Split into signing and encryption keys (Fernet order)
    const signingKey = hashBytes.slice(0, 16);
    const encryptionKey = hashBytes.slice(16);

    // Decode the double-encoded base64 response
    const base64urlString = typeof atob !== 'undefined' 
      ? atob(encryptedResponse)
      : Buffer.from(encryptedResponse, 'base64').toString('utf-8');
    
    // Convert base64url to standard base64
    let standardBase64 = base64urlString
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const padding = (4 - (standardBase64.length % 4)) % 4;
    standardBase64 += '='.repeat(padding);
    
    // Decode to get raw Fernet token bytes
    const tokenBytes = base64ToBytes(standardBase64);

    if (tokenBytes.length < 57) {
      throw new Error(`Token too short: ${tokenBytes.length} bytes`);
    }

    // Extract Fernet token components
    const version = tokenBytes[0];
    const timestamp = tokenBytes.slice(1, 9);
    const iv = tokenBytes.slice(9, 25);
    const ciphertext = tokenBytes.slice(25, tokenBytes.length - 32);
    const receivedHmac = tokenBytes.slice(-32);

    if (version !== 0x80) {
      throw new Error(`Invalid Fernet version: 0x${version.toString(16)}`);
    }

    // Verify HMAC
    const signedPortion = tokenBytes.slice(0, tokenBytes.length - 32);

    const hmacKeyForWebCrypto = await crypto.subtle.importKey(
      'raw',
      signingKey,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );

    const computedHmacBuffer = await crypto.subtle.sign(
      'HMAC',
      hmacKeyForWebCrypto,
      signedPortion
    );
    const computedHmac = new Uint8Array(computedHmacBuffer);

    // Compare HMACs
    if (computedHmac.length !== receivedHmac.length) {
      throw new Error('HMAC length mismatch');
    }

    for (let i = 0; i < computedHmac.length; i++) {
      if (computedHmac[i] !== receivedHmac[i]) {
        throw new Error('HMAC verification failed');
      }
    }

    // Decrypt ciphertext with AES-128-CBC
    const aesKey = await crypto.subtle.importKey(
      'raw',
      encryptionKey,
      { name: 'AES-CBC' },
      false,
      ['decrypt']
    );

    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: 'AES-CBC', iv },
      aesKey,
      ciphertext
    );

    // Decode UTF-8 and parse JSON
    const decoder = new TextDecoder();
    const decryptedText = decoder.decode(decryptedBuffer);
    
    return JSON.parse(decryptedText);

  } catch (error) {
    throw new Error(`Decryption failed: ${error.message}`);
  }
}

// Decrypt Fernet-encrypted data from backend
export const decryptData = async (encryptedData) => {
  if (!SECRET_KEY) {
    throw new Error('Decryption key not configured');
  }

  try {
    return await decryptAccountInfo(encryptedData, SECRET_KEY);
  } catch (error) {
    console.error('[Decryption] Failed:', error.message);
    throw new Error(`Failed to decrypt data: ${error.message}`);
  }
};
